import { useEffect } from "react";

import { isExecutableLanguage } from "@corca/tiptap";
import { comparer, observable, reaction, runInAction } from "mobx";

import { getUndoManager } from "@/components/tiptap/Collaboration";
import { rememberAppClipboardCopy } from "@/components/tiptap/copy-paste/appClipboardCopy";
import { applyPendingGridAction } from "@/components/tiptap/grid-controls/gridTarget";
import {
  createBlock,
  insertFile,
  insertImageFile,
  insertMathFromLatex,
  moveSelectionToEnd,
  setAlign,
  setBlockType,
  setFontSize,
  type BlockTextType,
  type CreateBlockType,
  type EditorAlignment,
} from "@/components/tiptap/utils/editorActions";
import { serializeForClipboard } from "@/components/tiptap/utils/handleCut";
import { isInsideMobileWebView } from "@/utils/mobileBridgeAuth";

import type { GridAction, GridAxis } from "@/components/tiptap/grid-controls/gridTarget";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Slice } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import type { Editor } from "@tiptap/react";

/**
 * Context for the code block the caret sits in — pushed to the mobile toolbar so
 * it can offer Run / Run all for executable (Python) cells. `null` when the caret
 * isn't in a code block. `isBusy` tracks *this* cell's run state so Run can disable
 * itself while queued/running; `runAllBusy` tracks the whole session (any busy cell
 * or a starting kernel) so Run all disables in lockstep with the web status bar.
 */
type CodeBlockContext = {
  cellId: string;
  language: string;
  isExecutable: boolean;
  isBusy: boolean;
  runAllBusy: boolean;
};

type CorcaBridge = {
  editorReady: () => void;
  setCursorInMath: (value: boolean) => void;
  setCursorInCodeBlock: (info: CodeBlockContext | null) => void;
  setTitle: (title: string) => void;
  setActiveTools: (keys: string[]) => void;
  setTextSize: (value: string) => void;
  setSelection: (from: number, to: number) => void;
  setBlockAlign: (value: string) => void;
  setUndoState: (canUndo: boolean, canRedo: boolean) => void;
  setActiveTools123: () => void;
  openGridMenu: (info: { axis: GridAxis; canRemove: boolean; canCopy: boolean }) => void;
  copyToClipboard: (content: { html: string; text: string }) => void;
  /** Whether the user can edit the doc; the app hides its insert UI when not. */
  setEditable: (editable: boolean) => void;
  /** Acknowledges an `appendFileChunk` so the app streams the next one. */
  fileChunkReceived: (id: string) => void;
};

/** List / block-style toggles from the mobile "add list" menu. */
type ListBlockType = "bulletList" | "orderedList" | "taskList" | "blockquote" | "callout";

/**
 * Higher-level editor actions the mobile toolbar invokes over the bridge. These
 * live here (not in the RN-injected script) because they need the document
 * model, editor state, and image-upload pipeline that only exist on the web —
 * mirroring the sidebar's CreateBlocks / TextHeading / Alignment flows.
 */
type CorcaActions = {
  createBlock: (type: CreateBlockType) => void;
  setBlockType: (type: BlockTextType) => void;
  setAlign: (alignment: EditorAlignment) => void;
  setFontSize: (size: string) => void;
  toggleBlock: (type: ListBlockType) => void;
  applyToSelection: (from: number, to: number, command: string, arg: unknown) => void;
  insertImage: (
    dataBase64: string,
    name: string,
    mimeType: string,
    options?: { select?: boolean },
  ) => void;
  /**
   * Files can be far larger than one injected script should carry, so the app
   * streams them as base64 chunks under an id, then calls `insertFile`.
   */
  appendFileChunk: (id: string, chunkBase64: string) => void;
  insertFile: (id: string, name: string, mimeType: string) => void;
  insertMathNode: (latex: string) => void;
  runCodeCell: (cellId: string) => void;
  runAllCells: () => void;
  applyGridAction: (action: GridAction) => void;
};

// Keys mirror the `activeKey` fields the RN EditorToolbar buttons declare.
// Order is stable so the join-key dedup in reportActiveTools stays consistent.
const ACTIVE_TOOL_CHECKS: ReadonlyArray<readonly [string, string, Record<string, unknown>?]> = [
  ["bold", "bold"],
  ["italic", "italic"],
  ["underline", "underline"],
  ["strike", "strike"],
  ["heading:1", "heading", { level: 1 }],
  ["heading:2", "heading", { level: 2 }],
  ["bulletList", "bulletList"],
  ["orderedList", "orderedList"],
];

type BridgeWindow = Window & {
  __corcaEditor?: Editor;
  __corcaDoc?: DocumentModel;
  __corcaActions?: CorcaActions;
  CorcaBridge?: CorcaBridge;
};

type BridgeCall = <K extends keyof CorcaBridge>(
  name: K,
  ...args: Parameters<CorcaBridge[K]>
) => void;

/**
 * The only way to call into `window.CorcaBridge`. The bridge object is injected
 * by the installed app, which may predate this web build: a method the app
 * doesn't know yet must be a no-op, never a crash — the whole editor unmounts to
 * Next's error page otherwise. The raw object is deliberately not kept in scope
 * so a direct `bridge.method()` call cannot be written by accident.
 */
function createBridgeCaller(bridge: CorcaBridge | undefined): BridgeCall {
  return (name, ...args) => {
    const fn = bridge?.[name];
    if (typeof fn === "function") {
      (fn as (...fnArgs: typeof args) => void).apply(bridge, args);
    }
  };
}

/** Calls into the installed app's bridge; a no-op outside the WebView. */
export const callMobileBridge: BridgeCall = (name, ...args) =>
  createBridgeCaller((window as BridgeWindow).CorcaBridge)(name, ...args);

/** Copies like Cmd+C would, but into the app's clipboard — see `copyToClipboard`. */
function writeSliceToAppClipboard(view: EditorView, slice: Slice) {
  const { dom, text } = serializeForClipboard(view, slice);
  rememberAppClipboardCopy(dom.innerHTML);
  callMobileBridge("copyToClipboard", { html: dom.innerHTML, text });
}

/** Whether the installed app is new enough to handle `name`. */
export function hasMobileBridgeMethod(name: keyof CorcaBridge) {
  return (
    typeof window !== "undefined" &&
    typeof (window as BridgeWindow).CorcaBridge?.[name] === "function"
  );
}

/**
 * Wires the TipTap editor up to the React Native WebView bridge.
 *
 *  - Exposes `__corcaEditor` / `__corcaDoc` so RN-injected JS can call commands.
 *  - Pushes editor state to RN through `window.CorcaBridge` (set up by the
 *    bridge script injected on the RN side). The web app is the source of
 *    truth; the bridge is just a transport.
 *
 * No-op outside the RN WebView.
 */
export function useMobileBridge({ editor, doc }: { editor: Editor | null; doc: DocumentModel }) {
  useEffect(() => {
    if (!editor || typeof window === "undefined" || !isInsideMobileWebView()) {
      return undefined;
    }

    const w = window as BridgeWindow;
    w.__corcaEditor = editor;
    w.__corcaDoc = doc;
    // Content added before the user first places the caret goes to the document
    // end (see @moveSelectionToEnd).
    let caretPlaced = editor.isFocused;
    const markCaretPlaced = () => {
      caretPlaced = true;
    };
    const call = createBridgeCaller(w.CorcaBridge);
    w.__corcaActions = createActions(editor, doc, () => caretPlaced, call);
    let lastInMath: boolean | null = null;
    let lastActiveKey: string | null = null;
    let lastSelectionKey: string | null = null;
    let lastUndoKey: string | null = null;

    const reportCursorInMath = () => {
      const active = document.activeElement;
      const inMath =
        active?.tagName === "INPUT" || (!!active && editor.isActive("inlineExpression"));
      if (inMath === lastInMath) return;
      lastInMath = inMath;
      call("setCursorInMath", inMath);
    };

    const reportTitle = () => {
      const firstNode = editor.state.doc.firstChild;
      call("setTitle", firstNode ? firstNode.textContent : "");
    };

    const reportActiveTools = () => {
      const active = ACTIVE_TOOL_CHECKS.filter(([, name, attrs]) =>
        editor.isActive(name, attrs),
      ).map(([key]) => key);
      const dedupKey = active.join("|");
      if (dedupKey === lastActiveKey) return;
      lastActiveKey = dedupKey;
      call("setActiveTools", active);
    };

    const reportSelection = () => {
      const { from, to } = editor.state.selection;
      const key = `${from}:${to}`;
      if (key === lastSelectionKey) return;
      lastSelectionKey = key;
      call("setSelection", from, to);
    };

    // Undo/redo availability for the header's undo menu. The yjs UndoManager owns
    // both stacks and emits on every push/pop/clear, so we mirror its own flags
    // instead of tracking editor transactions.
    const undoManager = getUndoManager(editor.state);
    const reportUndoState = () => {
      const canUndo = undoManager.canUndo();
      const canRedo = undoManager.canRedo();
      const key = `${canUndo}:${canRedo}`;
      if (key === lastUndoKey) return;
      lastUndoKey = key;
      call("setUndoState", canUndo, canRedo);
    };

    // Code-block context for the mobile Run buttons. A code block's CodeMirror is
    // a *separate* editor inside a contentEditable=false NodeView, so focusing it
    // never moves the outer ProseMirror selection into the block — `editor.isActive`
    // and `editorState.codeBlockAttrs` lag behind by one selection. So we track the
    // focused block from the DOM (`data-cell-id`, set in CodeBlockLineWrapper) and
    // stash it in an observable; the reaction folds in the live marimo status.
    const activeCodeCell = observable.box<{ cellId: string; language: string } | null>(null, {
      deep: false,
    });
    const updateActiveCodeCell = () => {
      const active = document.activeElement;
      const el = active instanceof Element ? active.closest("[data-cell-id]") : null;
      const cellId = el?.getAttribute("data-cell-id") ?? "";
      const language = el?.getAttribute("data-language") ?? "";
      const next = cellId ? { cellId, language } : null;
      const current = activeCodeCell.get();
      if (current?.cellId === next?.cellId && current?.language === next?.language) return;
      runInAction(() => activeCodeCell.set(next));
    };

    // Focus events bubble synchronously, but `document.activeElement` is only
    // updated *after* focusout — defer with a microtask so we read the new
    // active element, not the old one.
    const onFocusChange = () => queueMicrotask(reportCursorInMath);

    // Only refresh the active code cell on focus*in* (deferred so activeElement is
    // settled): focusing another element — a text block or a different cell — fires
    // focusin and updates it, while tapping the native Run button blurs the WebView
    // (a focusout with no focusin) and must NOT clear the block, or the Run buttons
    // would vanish the instant they're tapped.
    const onCodeFocusIn = () => queueMicrotask(updateActiveCodeCell);

    // Before `editorReady`, so the app never shows insert UI on a read-only doc.
    call("setEditable", doc.editable);
    call("editorReady");
    reportTitle();
    reportCursorInMath();
    reportActiveTools();
    reportSelection();
    reportUndoState();
    updateActiveCodeCell();

    // `editorState` is a MobX observable updated through Tiptap's useEditorState
    // render cycle, so it lags the raw `transaction`/`selectionUpdate` events the
    // other reporters hook. Observe it with a reaction instead so the new font
    // size is pushed the moment it actually changes — including right after the
    // toolbar applies one (a collapsed cursor stores the mark, which the editor
    // events wouldn't surface until the next keystroke).
    const disposeTextSize = reaction(
      () => doc.editor.editorState.textSize ?? "",
      (textSize) => call("setTextSize", textSize),
      { fireImmediately: true },
    );

    // Same lag applies to block alignment — observe the editorState observable
    // so the toolbar's alignment button reflects the current node's alignment.
    const disposeBlockAlign = reaction(
      () => doc.editor.editorState.blockAlign ?? "",
      (blockAlign) => call("setBlockAlign", blockAlign),
      { fireImmediately: true },
    );

    const disposeEditable = reaction(
      () => doc.editable,
      (editable) => call("setEditable", editable),
    );

    const disposeCodeBlock = reaction(
      (): CodeBlockContext | null => {
        const cell = activeCodeCell.get();
        if (!cell) return null;
        const status = doc.marimo.cellStatuses.get(cell.cellId);
        // Mirror the web status bar's Run-all gate: any busy cell, or a kernel
        // still starting, disables Run all.
        const hasBusyCell = Array.from(doc.marimo.cellStatuses.values()).some(
          (s) => s === "running" || s === "queued",
        );
        return {
          cellId: cell.cellId,
          language: cell.language,
          isExecutable: isExecutableLanguage(cell.language),
          isBusy: status === "running" || status === "queued",
          runAllBusy: doc.marimo.sessionStatus === "starting" || hasBusyCell,
        };
      },
      (info) => call("setCursorInCodeBlock", info),
      { fireImmediately: true, equals: comparer.structural },
    );

    editor.on("selectionUpdate", reportCursorInMath);
    editor.on("selectionUpdate", reportActiveTools);
    editor.on("selectionUpdate", reportSelection);
    editor.on("transaction", reportActiveTools);
    editor.on("transaction", reportSelection);
    editor.on("update", reportTitle);
    editor.on("focus", markCaretPlaced);
    undoManager.on("stack-item-added", reportUndoState);
    undoManager.on("stack-item-popped", reportUndoState);
    undoManager.on("stack-item-updated", reportUndoState);
    undoManager.on("stack-cleared", reportUndoState);
    document.addEventListener("focusin", onFocusChange);
    document.addEventListener("focusout", onFocusChange);
    document.addEventListener("focusin", onCodeFocusIn);

    return () => {
      disposeTextSize();
      disposeBlockAlign();
      disposeCodeBlock();
      disposeEditable();
      editor.off("selectionUpdate", reportCursorInMath);
      editor.off("selectionUpdate", reportActiveTools);
      editor.off("selectionUpdate", reportSelection);
      editor.off("transaction", reportActiveTools);
      editor.off("transaction", reportSelection);
      editor.off("update", reportTitle);
      editor.off("focus", markCaretPlaced);
      undoManager.off("stack-item-added", reportUndoState);
      undoManager.off("stack-item-popped", reportUndoState);
      undoManager.off("stack-item-updated", reportUndoState);
      undoManager.off("stack-cleared", reportUndoState);
      document.removeEventListener("focusin", onFocusChange);
      document.removeEventListener("focusout", onFocusChange);
      document.removeEventListener("focusin", onCodeFocusIn);
      delete w.__corcaEditor;
      delete w.__corcaDoc;
      delete w.__corcaActions;
    };
  }, [editor, doc]);
}

function base64ToBytes(dataBase64: string) {
  const byteChars = atob(dataBase64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return bytes;
}

/**
 * Builds the editor actions exposed to the mobile toolbar. The block/heading/
 * alignment/font-size/image actions delegate to the shared @editorActions so
 * they stay identical to the web sidebar; `toggleBlock` and `applyToSelection`
 * are bridge-specific (no direct sidebar twin) and stay here.
 */
function createActions(
  editor: Editor,
  doc: DocumentModel,
  isCaretPlaced: () => boolean,
  call: BridgeCall,
): CorcaActions {
  const pendingFileChunks = new Map<string, BlobPart[]>();

  const moveSelectionToEndIfCaretUnplaced = () => {
    if (!isCaretPlaced()) moveSelectionToEnd(editor);
  };

  return {
    createBlock(type) {
      if (!doc.editable) return;
      moveSelectionToEndIfCaretUnplaced();
      createBlock(doc, type);
    },
    setBlockType: (type) => setBlockType(doc, type),
    setAlign: (alignment) => setAlign(doc, alignment),
    setFontSize: (size) => setFontSize(doc, size),

    applyToSelection(from, to, command, arg) {
      // Re-select the exact range the user had highlighted before they tapped
      // the toolbar (the WebView has since lost focus), then run the command so
      // the formatting lands on that range — keeping the selection so they can
      // chain more formatting on it.
      const chain = editor.chain().setTextSelection({ from, to }) as unknown as Record<
        string,
        (a?: unknown) => { run: () => boolean }
      >;
      const runner = chain[command];
      if (typeof runner !== "function") return;
      (arg === null || arg === undefined ? runner() : runner(arg)).run();
    },

    toggleBlock(type) {
      const chain = editor.chain().focus();
      switch (type) {
        case "bulletList":
          chain.toggleBulletList().run();
          return;
        case "orderedList":
          chain.toggleOrderedList().run();
          return;
        case "taskList":
          chain.toggleTaskList().run();
          return;
        case "blockquote":
          // Quote and callout are mutually exclusive — drop the other first, as
          // the slash menu and sidebar do.
          chain.unsetCallout().toggleBlockquote().run();
          return;
        case "callout":
          chain.unsetBlockquote().toggleCallout().run();
          return;
      }
    },

    insertImage(dataBase64, name, mimeType, options) {
      if (!doc.editable) return;
      // Native side hands us base64; rebuild a File and run the shared upload flow.
      const file = new File([base64ToBytes(dataBase64)], name, { type: mimeType });
      moveSelectionToEndIfCaretUnplaced();
      insertImageFile(doc, file, undefined, options);
    },

    appendFileChunk(id, chunkBase64) {
      if (!doc.editable) return;
      const chunks = pendingFileChunks.get(id) ?? [];
      chunks.push(base64ToBytes(chunkBase64));
      pendingFileChunks.set(id, chunks);
      call("fileChunkReceived", id);
    },

    insertFile(id, name, mimeType) {
      const chunks = pendingFileChunks.get(id);
      pendingFileChunks.delete(id);
      if (!chunks || !doc.editable) return;
      moveSelectionToEndIfCaretUnplaced();
      insertFile(doc, new File(chunks, name, { type: mimeType }));
    },

    insertMathNode: (latex) => insertMathFromLatex(doc, latex),

    runCodeCell: (cellId) => doc.marimo.runCells([cellId]),
    runAllCells: () => doc.marimo.runCells(),

    applyGridAction: (action) => applyPendingGridAction(doc, action, writeSliceToAppClipboard),
  };
}
